import React from "react";
import {
  createPullPlan,
  deletePullPlan,
  fetchPullPlanSectionContext,
  fetchPullPlanTaktSectionContext,
  fetchTaktSectionWbsNodeIds,
  fetchV2Schedules,
  getPullPlan,
} from "../../../api/clientV2";
import type {
  PullPlanEntryMode,
  PullPlanKind,
  PullPlanMode,
  PullPlanSectionContext,
  PullPlanSeed,
  V2Schedule,
} from "../../../models";
import { readPayloadMode } from "../../../models";
import Spinner from "../../../components/Spinner/Spinner";
import { apiErrorMessage } from "../../../utils/apiError";
import PartitionView from "../EntryFlow/PartitionView/PartitionView";
import PullPlanHistory from "../PullPlanHistory/PullPlanHistory";
import PullPlanSectionsTab from "../PullPlanSectionsTab/PullPlanSectionsTab";
import PullPlanTabs, { type PullPlanTab } from "../PullPlanTabs/PullPlanTabs";
import SectionView from "../SectionView/SectionView";
import "./PullPlanPage.css";

interface PullPlanPageProps {
  initialSelection?: PullPlanSeed | null;
  /** Called once the seed has been consumed (state seeded from it) so the
   *  parent can null it. Without this, the seed re-applies on every remount
   *  — e.g. when the user navigates away and returns, they get teleported
   *  back into the seeded section. */
  onSeedConsumed?: () => void;
}

const PullPlanPage: React.FC<PullPlanPageProps> = ({
  initialSelection,
  onSeedConsumed,
}) => {
  const [scheduleId, setScheduleId] = React.useState<number | null>(null);
  const [activeTab, setActiveTab] = React.useState<PullPlanTab>("sections");
  // Exactly one of these identifies the open section: a Takt section name
  // (sections tab / takt board) or a WBS node id (history rows).
  const [taktSectionName, setTaktSectionName] = React.useState<string | null>(
    initialSelection?.sectionName ?? null,
  );
  const [selectedWbsId, setSelectedWbsId] = React.useState<number | null>(null);
  const [entryMode, setEntryMode] = React.useState<PullPlanEntryMode>(
    initialSelection?.mode ?? "browse",
  );
  // The adjust/repull fork a fresh launch should stamp into a new draft. Seeded
  // from a Takt-board "Plan" launch ("adjust"). Defaults to "adjust" so a
  // section auto-started without an explicit fork edits in place rather than
  // wiping. Consumed by SectionView's startPull when it creates a draft.
  const [seedPullMode, setSeedPullMode] = React.useState<PullPlanMode>(
    initialSelection?.pullMode ?? "adjust",
  );
  // Bumped after the user returns from the canvas so the Sections tab refetches
  // its drafts-by-section map (a draft may have been created or discarded).
  const [sectionsRefreshNonce, setSectionsRefreshNonce] = React.useState(0);

  // Applies any incoming seed (initial mount or in-page jump) and immediately
  // signals consumption so the parent can null it. Without the consumption
  // signal, a stale seed re-teleports the user into the seeded section on
  // every remount (e.g. nav away to Diff and back).
  const seedNonce = initialSelection?.nonce;
  React.useEffect(() => {
    if (!initialSelection) return;
    setEntryMode(initialSelection.mode ?? "browse");
    setSeedPullMode(initialSelection.pullMode ?? "adjust");
    if (initialSelection.sectionName) {
      setTaktSectionName(initialSelection.sectionName);
      setSelectedWbsId(null);
    }
    onSeedConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedNonce]);
  const [context, setContext] = React.useState<PullPlanSectionContext | null>(null);
  const [loadingContext, setLoadingContext] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  // For TAKT-section pulls, resolve the canonical WBS id (first id under the
  // takt section) so SectionView can anchor pull_plans rows to a real
  // wbs_nodes.id. Null while resolving or while the section isn't TAKT-driven.
  const [taktAnchorWbsId, setTaktAnchorWbsId] = React.useState<number | null>(null);
  // False while the takt-section → wbs anchor is genuinely being resolved; true
  // once it settles (to an id OR null). Gates the loading spinner so a section
  // that resolves no anchor shows an explicit empty state, not a forever-spin.
  const [anchorSettled, setAnchorSettled] = React.useState(false);
  // Anchor for a plan created in the Pull-by-WBS-leaves modal (the first
  // pulled leaf). A fresh section name resolves no WBS ids of its own yet, so
  // the takt-section anchor lookup can't serve these — the explicit anchor
  // rides along from the creation flow instead.
  const [leafAnchorWbsId, setLeafAnchorWbsId] = React.useState<number | null>(null);
  // Carries a pull_plan id when the user opened the section by clicking a
  // draft row in the Pull History tab. SectionView auto-enters Plan mode on
  // this draft, then clears the value via onPendingDraftConsumed so a
  // subsequent Back+Open of the same section opens in Browse mode again.
  const [pendingDraftId, setPendingDraftId] = React.useState<number | null>(null);

  // Stable identities for the SectionView callback props so SectionView's
  // consume-effect (which depends on `onInitialDraftConsumed`) doesn't re-fire
  // on every parent render.
  const handleReloadContext = React.useCallback(() => setRefreshKey((k) => k + 1), []);
  const handleViewHistoryFromSection = React.useCallback(() => {
    setSelectedWbsId(null);
    setTaktSectionName(null);
    setActiveTab("history");
  }, []);
  const handleInitialDraftConsumed = React.useCallback(
    () => setPendingDraftId(null),
    [],
  );
  const handleOpenSectionFromHistory = React.useCallback(
    (wbsId: number, draftIdToResume: number | null) => {
      // Don't force the Sections tab: mounting PullPlanSectionsTab fires its
      // eager fetch of every draft's full payload, which competes with and
      // delays the draft's own canvas load (leaving the canvas blank). The
      // canvas renders over whichever tab is active; Back returns to it.
      setSelectedWbsId(wbsId);
      setTaktSectionName(null);
      setLeafAnchorWbsId(null);
      // Resuming a draft → Plan; otherwise just browse the past pulls.
      setEntryMode(draftIdToResume != null ? "plan" : "browse");
      setPendingDraftId(draftIdToResume);
    },
    [],
  );
  const enterSection = React.useCallback(
    (name: string, mode: PullPlanEntryMode, draftId: number | null) => {
      setTaktSectionName(name);
      setSelectedWbsId(null);
      setLeafAnchorWbsId(null);
      setEntryMode(mode);
      setPendingDraftId(draftId);
    },
    [],
  );
  // A freshly-created leaf plan opens on the ENTRY page (partition + pull
  // target) instead of the board; Start Plan hands off to the board.
  const [entryFlow, setEntryFlow] = React.useState<{
    sectionName: string;
    draftId: number;
    anchorWbsId: number;
    /** This draft was created fresh for THIS entry session (not resumed). Backing
     *  out cancels it, so the draft is discarded rather than orphaned. */
    createdFresh?: boolean;
  } | null>(null);
  const handleLeafPlanCreated = React.useCallback(
    (args: {
      sectionName: string;
      draftId: number;
      anchorWbsId: number;
      kind: PullPlanKind;
    }) => {
      // A plain (non-Takt) plan has no Takt_SectionName, so it can't ride the
      // takt-section entry/resolve path. Open it directly on its WBS anchor in
      // Plan mode — same path the Pull History rows use.
      if (args.kind === "plain") {
        handleOpenSectionFromHistory(args.anchorWbsId, args.draftId);
        return;
      }
      setEntryFlow({
        sectionName: args.sectionName,
        draftId: args.draftId,
        anchorWbsId: args.anchorWbsId,
        createdFresh: true,
      });
    },
    [handleOpenSectionFromHistory],
  );
  // Opening an existing plain plan from a section card: route through the
  // WBS-id anchor path (never enterSection — there's no Takt section name).
  const handleOpenPlainPlan = React.useCallback(
    (anchorWbsId: number, draftId: number) => {
      handleOpenSectionFromHistory(anchorWbsId, draftId);
    },
    [handleOpenSectionFromHistory],
  );
  const handleEntryStartPlan = React.useCallback(() => {
    setEntryFlow((args) => {
      if (args) {
        setTaktSectionName(args.sectionName);
        setSelectedWbsId(null);
        setLeafAnchorWbsId(args.anchorWbsId);
        setEntryMode("plan");
        setPendingDraftId(args.draftId);
      }
      return null;
    });
  }, []);
  const handleEntryBack = React.useCallback(() => {
    // Backing out of a freshly-created entry draft CANCELS it — the partition
    // gate may have already staged "replace all not-started" deletes, so leaving
    // the draft would orphan an unreviewed, half-built plan on the section card.
    if (entryFlow?.createdFresh && scheduleId !== null) {
      void deletePullPlan(scheduleId, entryFlow.draftId).catch(() => {});
    }
    setEntryFlow(null);
    setSectionsRefreshNonce((n) => n + 1);
  }, [entryFlow, scheduleId]);
  // A takt section is the same concept as a leaf selection — its WBS nodes —
  // so starting a NEW pull from a section card also goes through the entry
  // page: create the draft here (scope auto-resolved from the takt section),
  // then open the partition view on it.
  const startingPullRef = React.useRef(false);
  const handleStartPullFromSection = React.useCallback(
    async (name: string, mode: PullPlanMode) => {
      if (scheduleId === null || startingPullRef.current) return;
      startingPullRef.current = true;
      try {
        const ids = await fetchTaktSectionWbsNodeIds(scheduleId, name);
        const anchor = ids[0];
        if (anchor == null) {
          enterSection(name, "plan", null);
          return;
        }
        const today = new Date().toLocaleDateString(undefined, {
          month: "numeric",
          day: "numeric",
        });
        const created = await createPullPlan(scheduleId, {
          section_wbs_id: anchor,
          name: `${name} — ${today}`,
          takt_section_name: name,
          payload_json: JSON.stringify({ kind: "takt", mode }),
        });
        // Re-pull walks the partition/pull-target gate before the board; Adjust
        // skips the sweep and opens straight to the board on the new draft.
        if (mode === "repull") {
          setEntryFlow({
            sectionName: name,
            draftId: created.id,
            anchorWbsId: anchor,
            createdFresh: true,
          });
        } else {
          enterSection(name, "plan", created.id);
        }
      } catch (e: unknown) {
        setError(apiErrorMessage(e, "Failed to start pull plan."));
      } finally {
        startingPullRef.current = false;
      }
    },
    [scheduleId, enterSection],
  );
  // Continuing a draft that never finished entry (partitioned but no pull
  // target chosen) resumes the ENTRY page; anything past that goes to the
  // board as before.
  const handleContinuePullFromSection = React.useCallback(
    async (name: string, draftId: number) => {
      if (scheduleId === null) return;
      try {
        const plan = await getPullPlan(scheduleId, draftId);
        const { mode } = readPayloadMode(plan.payload_json);
        // A re-pull whose entry gate never completed (partition done, pull
        // target unchosen) resumes the gate; everything else goes to the board.
        const raw: any = JSON.parse(plan.payload_json || "{}");
        if (mode === "repull" && raw.entry_partition && !raw.pull_target) {
          setEntryFlow({
            sectionName: name,
            draftId,
            anchorWbsId: plan.section_wbs_id,
          });
          return;
        }
      } catch {
        // fall through to the board
      }
      enterSection(name, "plan", draftId);
    },
    [scheduleId, enterSection],
  );
  const handleBackFromCanvas = React.useCallback(() => {
    setSelectedWbsId(null);
    setTaktSectionName(null);
    setLeafAnchorWbsId(null);
    setPendingDraftId(null);
    setEntryMode("browse");
    // Force the Sections tab to refetch its drafts map — a draft may have
    // just been created or discarded on the canvas.
    setSectionsRefreshNonce((n) => n + 1);
  }, []);

  // Resolve a wbs anchor id for TAKT-driven section opens. Sections-tab
  // navigation sets taktSectionName; for pull_plans persistence we need a
  // real wbs_nodes.id to anchor on. Picks the first id returned by the
  // backend's takt-section → wbs lookup (sorted lowest-id-first).
  React.useEffect(() => {
    if (scheduleId === null || !taktSectionName) {
      setTaktAnchorWbsId(null);
      setAnchorSettled(true);
      return;
    }
    if (leafAnchorWbsId !== null) {
      setTaktAnchorWbsId(leafAnchorWbsId);
      setAnchorSettled(true);
      return;
    }
    let cancelled = false;
    setAnchorSettled(false);
    fetchTaktSectionWbsNodeIds(scheduleId, taktSectionName)
      .then(async (ids) => {
        if (cancelled) return;
        if (ids[0] != null) {
          setTaktAnchorWbsId(ids[0]);
          setAnchorSettled(true);
          return;
        }
        // The takt-name → wbs lookup found nothing (e.g. the draft's section
        // name no longer matches any activity's Takt_SectionName). Fall back to
        // the draft's own stored anchor so the board can still open.
        if (pendingDraftId !== null) {
          try {
            const plan = await getPullPlan(scheduleId, pendingDraftId);
            if (cancelled) return;
            setTaktAnchorWbsId(plan.section_wbs_id ?? null);
            setAnchorSettled(true);
            return;
          } catch {
            // fall through to the empty state
          }
        }
        if (!cancelled) {
          setTaktAnchorWbsId(null);
          setAnchorSettled(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTaktAnchorWbsId(null);
          setAnchorSettled(true);
        }
      });
    return () => { cancelled = true; };
  }, [scheduleId, taktSectionName, leafAnchorWbsId, pendingDraftId]);

  React.useEffect(() => {
    let cancelled = false;
    fetchV2Schedules()
      .then((scheds: V2Schedule[]) => {
        if (cancelled) return;
        if (scheds.length === 0) setError("No schedule available.");
        else setScheduleId(scheds[0].id);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message ?? "Failed to load schedules.");
      });
    return () => { cancelled = true; };
  }, []);

  // Whenever the selection changes, fetch the appropriate context payload.
  // TAKT-section-driven pull → endpoint that filters by Takt_SectionName.
  // WBS-id-driven open (history rows) → the WBS subtree context.
  React.useEffect(() => {
    if (scheduleId === null) {
      setContext(null);
      return;
    }
    if (!taktSectionName && selectedWbsId === null) {
      setContext(null);
      return;
    }
    let cancelled = false;
    setLoadingContext(true);
    const fetchContext = taktSectionName
      ? fetchPullPlanTaktSectionContext(scheduleId, taktSectionName)
      : fetchPullPlanSectionContext(scheduleId, selectedWbsId as number);
    fetchContext
      .then((ctx) => { if (!cancelled) setContext(ctx); })
      .catch((e) => { if (!cancelled) setError(e.message ?? "Failed to load section."); })
      .finally(() => { if (!cancelled) setLoadingContext(false); });
    return () => { cancelled = true; };
  }, [scheduleId, selectedWbsId, taktSectionName, refreshKey]);

  if (scheduleId === null) {
    return (
      <div className="pull-plan-page">
        {error ? <div className="pull-plan-error">{error}</div> : <Spinner visible label="Loading…" />}
      </div>
    );
  }

  const onCanvas = selectedWbsId !== null || taktSectionName !== null;
  const sectionAnchorWbsId =
    taktSectionName !== null ? taktAnchorWbsId : selectedWbsId;

  if (entryFlow) {
    return (
      <div className="pull-plan-page">
        <PartitionView
          scheduleId={scheduleId}
          draftId={entryFlow.draftId}
          sectionName={entryFlow.sectionName}
          onBack={handleEntryBack}
          onStartPlan={handleEntryStartPlan}
        />
      </div>
    );
  }

  // Keep the selector mounted while on the canvas so its drill depth + scroll
  // position survive the round-trip. CSS hides it; React doesn't unmount it.
  return (
    <div className="pull-plan-page">
      <div className="pull-plan-page-stack" style={{ display: onCanvas ? "none" : "flex" }}>
        <PullPlanTabs active={activeTab} onChange={setActiveTab} />
        {activeTab === "sections" && (
          <PullPlanSectionsTab
            scheduleId={scheduleId}
            refreshNonce={sectionsRefreshNonce}
            onStartPull={handleStartPullFromSection}
            onContinuePull={handleContinuePullFromSection}
            onLeafPlanCreated={handleLeafPlanCreated}
            onOpenPlainPlan={handleOpenPlainPlan}
          />
        )}
        {activeTab === "history" && (
          <PullPlanHistory
            scheduleId={scheduleId}
            refreshNonce={sectionsRefreshNonce}
            onOpenSection={handleOpenSectionFromHistory}
            onResumeTaktDraft={handleContinuePullFromSection}
          />
        )}
      </div>

      {onCanvas && (
        <div className="pull-plan-page-stack">
          {error && <div className="pull-plan-error">{error}</div>}
          <Spinner
            visible={
              (loadingContext && !context) ||
              (taktSectionName !== null && !anchorSettled)
            }
            label="Loading section..."
          />
          {context &&
            sectionAnchorWbsId === null &&
            anchorSettled &&
            !loadingContext && (
              <div className="pull-plan-error">
                Couldn&apos;t locate this section&apos;s WBS anchor — its
                activities may have been renamed, re-coded, or moved out of the
                section.{" "}
                <button
                  type="button"
                  className="pull-plan-error-back"
                  onClick={handleBackFromCanvas}
                >
                  ← Back to plans
                </button>
              </div>
            )}
          {context && sectionAnchorWbsId !== null && (
            <SectionView
              scheduleId={scheduleId}
              sectionContext={context}
              sectionAnchorWbsId={sectionAnchorWbsId}
              entryMode={entryMode}
              pullMode={seedPullMode}
              onBack={handleBackFromCanvas}
              onReloadContext={handleReloadContext}
              depEditorSection={taktSectionName ?? undefined}
              onViewHistory={handleViewHistoryFromSection}
              onAfterDiscard={handleBackFromCanvas}
              initialDraftId={pendingDraftId}
              onInitialDraftConsumed={handleInitialDraftConsumed}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PullPlanPage;
